import { EventProperties } from './types';

// Standard event names used throughout the application
export enum AppEvent {
  SUBSCRIPTION_SKIP_TRIAL = 'SUBSCRIPTION_SKIP_TRIAL',
  SUBSCRIPTION_FREE_TRIAL = 'SUBSCRIPTION_FREE_TRIAL',
  SUBSCRIPTION_PURCHASED = 'SUBSCRIPTION_PURCHASED',
  SUBSCRIPTION_UPGRADED = 'SUBSCRIPTION_UPGRADED',
  SUBSCRIPTION_DOWNGRADED = 'SUBSCRIPTION_DOWNGRADED',
  PURCHASE_CHANNEL = 'PURCHASE_CHANNEL',
  // v2
  CAMERA_NAME = 'CAMERA_NAME',
  INITIATE_PURCHASE = 'INITIATE_PURCHASE',
  SKIP_PURCHASE = 'SKIP_PURCHASE',
  SKIP_TRIAL = 'SKIP_TRIAL',
  SELECT_BILLING_CYCLE = 'SELECT_BILLING_CYCLE',
  SELECT_PLAN = 'SELECT_PLAN',
  INITIATE_PLAN_PURCHASE = 'INITIATE_PLAN_PURCHASE',
  PAYMENT_SELECTION = 'PAYMENT_SELECTION',
  CONFIRM_PURCHASE = 'CONFIRM_PURCHASE',
  PURCHASE_ACTIVATION_POPUP = 'PURCHASE_ACTIVATION_POPUP',
  PURCHASE_FAILURE_POPUP = 'PURCHASE_FAILURE_POPUP',
  NO_PURCHASE_NECESSARY_POPUP = 'NO_PURCHASE_NECESSARY_POPUP',
  YOU_ARE_COVERED_POPUP = 'YOU_ARE_COVERED_POPUP',
  PURCHASE_DOWNGRADE_POPUP = 'PURCHASE_DOWNGRADE_POPUP',
  PLAN_REACTIVATION_POPUP = 'PLAN_REACTIVATION_POPUP',
  MAX_CAMERA_POPUP = 'MAX_CAMERA_POPUP',
  DISMISS = 'DISMISS',
  REFRESH = 'REFRESH',
  BACK = 'BACK',
  SKIP_PURCHASE_4G = 'SKIP_PURCHASE_4G',
  SELECT_DATA_SUBSCRIPTION_BILLING_CYCLE = 'SELECT_DATA_SUBSCRIPTION_BILLING_CYCLE',
  AUTO_RECHARGE_TOGGLE = 'AUTO_RECHARGE_TOGGLE',
  SELECT_DATA_SUBSCRIPTION_PLAN = 'SELECT_DATA_SUBSCRIPTION_PLAN',
  SELECT_AUTO_DATA_RECHARGE_PLAN = 'SELECT_AUTO_DATA_RECHARGE_PLAN',
  INITIATE_DATA_SUBSCRIPTION_PURCHASE = 'INITIATE_DATA_SUBSCRIPTION_PURCHASE',
  REJECT_DATA_SUBSCRIPTION_PURCHASE = 'REJECT_DATA_SUBSCRIPTION_PURCHASE',
  CONFIRM_DATA_PURCHASE = 'CONFIRM_DATA_PURCHASE',
  DATA_PURCHASE_ACTIVATION_POPUP = 'DATA_PURCHASE_ACTIVATION_POPUP',
  DATA_PURCHASE_FAILURE_POPUP = 'DATA_PURCHASE_FAILURE_POPUP',
  SELECT_ONE_TIME_DATA_PLAN = 'SELECT_ONE_TIME_DATA_PLAN',
  INITIATE_ONE_TIME_DATA_PURCHASE = 'INITIATE_ONE_TIME_DATA_PURCHASE',
  CONFIRM_ONE_TIME_DATA_PURCHASE = 'CONFIRM_ONE_TIME_DATA_PURCHASE',
  CONFIRM_AUTO_RECHARGE = 'CONFIRM_AUTO_RECHARGE',
  AUTO_RECHARGE_CONFIRMATION_POPUP = 'AUTO_RECHARGE_CONFIRMATION_POPUP',
  CHOOSE_BASE_PLAN = 'CHOOSE_BASE_PLAN',
  CONFIRM_BASE_PLAN = 'CONFIRM_BASE_PLAN',
  SCREEN_VIEW = 'SCREEN_VIEW',
  // FCM-2514 retry-payment webview
  PAYMENT_RETRY = 'PAYMENT_RETRY',
  PAYMENT_RETRY_FAILURE_ACTION = 'PAYMENT_RETRY_FAILURE_ACTION',
  TAP_USE_DIFFERENT_CARD = 'TAP_USE_DIFFERENT_CARD',
  PAYMENT_RETRY_WITH_UPDATED_CARD = 'PAYMENT_RETRY_WITH_UPDATED_CARD',
  UPDATED_CARD_FAILURE_ACTION = 'UPDATED_CARD_FAILURE_ACTION',
  // A/B experiments: fired once per session when a user is assigned a variant
  EXPERIMENT_VIEWED = 'EXPERIMENT_VIEWED'
}

// Maps app's event names to service-specific event names
export const eventNameMap: Record<AppEvent, string> = {
  [AppEvent.SUBSCRIPTION_SKIP_TRIAL]: 'subscription-skip-trial',
  [AppEvent.SUBSCRIPTION_FREE_TRIAL]: 'subscription-free-trial',
  [AppEvent.SUBSCRIPTION_PURCHASED]: 'subscription-purchased',
  [AppEvent.SUBSCRIPTION_UPGRADED]: 'subscription-upgraded',
  [AppEvent.SUBSCRIPTION_DOWNGRADED]: 'subscription-downgraded',
  [AppEvent.PURCHASE_CHANNEL]: 'purchase-channel',

  // v2
  [AppEvent.CAMERA_NAME]: 'camera-name',
  [AppEvent.INITIATE_PURCHASE]: 'initiate-purchase',
  [AppEvent.SKIP_PURCHASE]: 'skip-purchase',
  [AppEvent.SKIP_TRIAL]: 'skip-trial',
  [AppEvent.SELECT_BILLING_CYCLE]: 'select-billing-cycle',
  [AppEvent.SELECT_PLAN]: 'select-plan',
  [AppEvent.INITIATE_PLAN_PURCHASE]: 'initiate-plan-purchase',
  [AppEvent.PAYMENT_SELECTION]: 'payment-selection',
  [AppEvent.CONFIRM_PURCHASE]: 'confirm-purchase',
  [AppEvent.PURCHASE_ACTIVATION_POPUP]: 'purchase-activation-pop-up',
  [AppEvent.PURCHASE_FAILURE_POPUP]: 'purchase-failure-pop-up',
  [AppEvent.NO_PURCHASE_NECESSARY_POPUP]: 'no-purchase-necessary-pop-up',
  [AppEvent.YOU_ARE_COVERED_POPUP]: 'you-are-covered-pop-up',
  [AppEvent.PURCHASE_DOWNGRADE_POPUP]: 'purchase-downgrade-pop-up',
  [AppEvent.PLAN_REACTIVATION_POPUP]: 'plan-reactivation-pop-up',
  [AppEvent.MAX_CAMERA_POPUP]: 'max-camera-pop-up',
  [AppEvent.DISMISS]: 'dismiss',
  [AppEvent.REFRESH]: 'refresh',
  [AppEvent.BACK]: 'back',
  [AppEvent.SKIP_PURCHASE_4G]: 'skip-purchase-4g',
  [AppEvent.SELECT_DATA_SUBSCRIPTION_BILLING_CYCLE]:
    'select-data-subscription-billing-cycle',
  [AppEvent.AUTO_RECHARGE_TOGGLE]: 'auto-recharge-toggle',
  [AppEvent.SELECT_DATA_SUBSCRIPTION_PLAN]: 'select-data-subscription-plan',
  [AppEvent.SELECT_AUTO_DATA_RECHARGE_PLAN]: 'select-auto-data-recharge-plan',
  [AppEvent.INITIATE_DATA_SUBSCRIPTION_PURCHASE]:
    'initiate-data-subscription-purchase',
  [AppEvent.REJECT_DATA_SUBSCRIPTION_PURCHASE]:
    'reject-data-subscription-purchase',
  [AppEvent.CONFIRM_DATA_PURCHASE]: 'confirm-data-purchase',
  [AppEvent.DATA_PURCHASE_ACTIVATION_POPUP]: 'data-purchase-activation-pop-up',
  [AppEvent.DATA_PURCHASE_FAILURE_POPUP]: 'data-purchase-failure-pop-up',
  [AppEvent.SELECT_ONE_TIME_DATA_PLAN]: 'select-one-time-data-plan',
  [AppEvent.INITIATE_ONE_TIME_DATA_PURCHASE]: 'initiate-one-time-data-purchase',
  [AppEvent.CONFIRM_ONE_TIME_DATA_PURCHASE]: 'confirm-one-time-data-purchase',
  [AppEvent.CONFIRM_AUTO_RECHARGE]: 'confirm-auto-recharge',
  [AppEvent.AUTO_RECHARGE_CONFIRMATION_POPUP]:
    'auto-recharge-confirmation-pop-up',
  [AppEvent.CHOOSE_BASE_PLAN]: 'choose-base-plan',
  [AppEvent.CONFIRM_BASE_PLAN]: 'confirm-base-plan',
  [AppEvent.SCREEN_VIEW]: 'screen-view',

  // FCM-2514 retry-payment webview. Names use the underscore casing the
  // analytics spec defines, intentionally differing from the kebab-case above.
  [AppEvent.PAYMENT_RETRY]: 'payment_retry',
  [AppEvent.PAYMENT_RETRY_FAILURE_ACTION]: 'payment_retry_failure_action',
  [AppEvent.TAP_USE_DIFFERENT_CARD]: 'tap_use_different_card',
  [AppEvent.PAYMENT_RETRY_WITH_UPDATED_CARD]: 'payment_retry_with_updated_card',
  [AppEvent.UPDATED_CARD_FAILURE_ACTION]: 'updated_card_failure_action',
  [AppEvent.EXPERIMENT_VIEWED]: 'experiment-viewed'
};

export const EVENT_PROPERTIES = {
  CHANNEL_NAME: 'Channel-Name',
  TRIAL_SKIPPED: 'Trial-Skipped',
  REASON_ID: 'Reason-ID',
  PLAN_NAME: 'Plan-Name',
  BILLING_CYCLE: 'Billing-Cycle',
  BILLING_DATE: 'Billing-Date',
  START_DATE: 'Start-Date',
  TRIAL_DAYS: 'Trial-Days',
  CURRENT_PLAN_NAME: 'Current-Plan-Name',
  NEXT_BILLING_DATE: 'Next-Billing-Date',
  DOWNGRADED_PLAN_NAME: 'Downgraded-Plan-Name',
  DOWNGRADED_DATE: 'Downgraded-Date',
  UPGRADED_PLAN_NAME: 'Upgraded-Plan-Name',
  UPGRADED_DATE: 'Upgraded-Date',
  IS_TRIAL: 'Is-Trial',

  // v2
  NAME: 'Name',
  FROM_SCREEN: 'From-Screen',
  PLAN_TYPE: 'Plan-Type',
  REASON: 'Reason',
  PLAN: 'Plan',
  PURCHASE_TYPE: 'Purchase-Type',
  PAYMENT_TYPE: 'Payment-Type',
  PURCHASE_PATH: 'Purchase-Path',
  SELECT: 'Select',
  POPUP_TYPE: 'Pop-Up-Type',
  SCREEN: 'Screen',
  AUTO_RECHARGE_ENABLED: 'Auto-Recharge-Enabled',
  AUTO_RECHARGE_PLAN: 'Auto-Recharge-Plan',
  PLAN_SELECTION: 'Plan-Selection',
  QUANTITY_OF_PLAN: 'Quantity-Of-Plan',
  SCREEN_NAME: 'Screen-Name',
  PRODUCT_TYPE: 'Product-Type',

  // FCM-2514 retry-payment. Casing matches the analytics spec verbatim
  // (Retry-Type hyphenated, Is_Successful with an underscore).
  RETRY_TYPE: 'Retry-Type',
  IS_SUCCESSFUL: 'Is_Successful',

  // A/B experiments (EXPERIMENT_VIEWED)
  EXPERIMENT: 'Experiment',
  VARIANT: 'Variant'
};

// Track event values

export enum PlanType {
  HOME_SECURITY = 'HomeSecurity',
  COMBO = 'Combo',
  BIRD_WATCH = 'Bird-Watch',
  PET_SENSE = 'Pet-Sense',
  TRAIL_WATCH = 'Trail-Watch',
  BABY_SENSE = 'Baby-Sense',
  DATA = 'Data'
}

export enum PurchaseType {
  TRIAL = 'Trial',
  PURCHASE = 'Purchase',
  UPGRADE = 'Upgrade',
  DOWNGRADE = 'Downgrade',
  REACTIVATE = 'Reactivate'
}

export enum FromScreen {
  PREMIUM_FEATURES_LISTING = 'Premium-Features-Listing',
  BASIC_FEATURES_LISTING = 'Basic-Features-Listing'
}

export enum PaymentType {
  APPLE_PAY = 'Apple Pay',
  GOOGLE_PAY = 'Google Pay',
  CREDIT_CARD = 'Credit Card'
}

// FCM-2514 retry-payment webview value enums.
export enum RetryType {
  TRIAL_TO_PAID = 'Trial-to-Paid',
  RENEWAL = 'Renewal'
}

// Select values on the retry / updated-card failure pop-ups.
export enum RetryFailureSelect {
  TRY_DIFFERENT_CARD = 'Try-Different-card',
  CANCEL = 'Cancel'
}

export enum BillingCycle {
  MONTHLY = 1,
  YEARLY = 12
}

export enum BasePlanSelection {
  AI_COMBO = 'AI-Combo',
  DATA_SUBSCRIPTION = 'Data-Subscription',
  ONE_TIME_DATA_PASS = 'One-Time-Data-Pass'
}

export enum DataPlanType {
  SUBSCRIPTION = 'Subscription',
  ONE_TIME_PASS = 'One-Time-Pass'
}

export enum ProductType {
  HOME_SECURITY = 'HomeSecurity',
  BIRD = 'Bird',
  PET = 'Pet',
  TRAIL = 'Trail',
  BABY = 'Baby'
}

export enum SelectType {
  GO_HOME = 'Go-Home',
  OKAY = 'Okay',
  CONTINUE_DOWNGRADE = 'Continue-Downgrade',
  GO_BACK_TO_PLAN = 'Go-Back-To-Plan',
  VIEW_UPGRADE_OPTIONS = 'View-Upgrade-Options',
  KEEP_CURRENT_PLAN = 'Keep-Current-Plan',
  DISABLE_AUTO_RECHARGE = 'Disable-Auto-Recharge',
  KEEP_AUTO_RECHARGE = 'Keep-Auto-Recharge',
  CONTINUE_WITH_PLAN = 'Continue-With-Plan'
}

export enum AutoRechargeToggle {
  ENABLE = 'Enable',
  DISABLE = 'Disable'
}

export enum AutoRechargeScreenName {
  DATA_SUBSCRIPTION = 'Data-Subscription',
  SMART_AUTO_DATA_RECHARGE = 'Smart-Auto-Data-Recharge'
}

export enum Skip4GSelect {
  DATA_SUBSCRIPTION = 'Data-Subscription',
  REJECT_PLAN = 'Reject-Plan'
}

export enum PopUpType {
  AUTO_ADD_CAMERA = 'Auto-Add-Camera',
  FAILED_TO_ADD_CAMERA = 'Failed-To-Add-Camera'
}

export type DataProductType = Extract<
  ProductType,
  ProductType.HOME_SECURITY | ProductType.TRAIL
>;

/** Shared shape for events that have From-Screen + PurchaseType + PlanType */
export interface PurchaseScreenProps {
  fromScreen: FromScreen;
  purchaseType: PurchaseType;
  planType: PlanType;
}

/** Shared shape for post-purchase pop-ups */
export interface PostPurchasePopUpProps {
  plan: string;
  purchaseType: PurchaseType;
  purchasePath: string;
}

/** Shared shape for data purchase events */
export interface DataPurchaseProps {
  plan?: string;
  autoRechargeEnabled?: boolean;
  autoRechargePlan?: string; // N/A when autoRechargeEnabled is false
  purchaseType?: Extract<
    PurchaseType,
    PurchaseType.PURCHASE | PurchaseType.UPGRADE | PurchaseType.TRIAL
  >;
  purchasePath: string;
}

export enum ScreenName {
  LOADING_SCREEN = 'Loading-Screen',
  CAMERA_NAMING = 'Camera-Naming',
  PREMIUM_FEATURES_LISTING = 'Premium-Features-Listing',
  BASIC_FEATURES_LISTING = 'Basic-Features-Listing',
  PLAN_LIST_FOR_PURCHASE = 'Plan-List-For-Purchase',
  PAYMENT_PAGE = 'Payment-Page',
  PURCHASE_CONFIRMATION = 'Purchase-Confirmation',
  PLAN_LIST_FOR_MODIFICATION = 'Plan-List-For-Modification',
  PAYMENT_FAILED = 'Payment-Failed',
  EXTEND_COVERAGE = 'Extend-Coverage',
  CHOOSE_BASE_PLAN = 'Choose-Base-Plan',
  DATA_SUBSCRIPTION = 'Data-Subscription',
  ONE_TIME_DATA_PASS = 'One-Time-Data-Pass',
  SMART_AUTO_DATA_RECHARGE = 'Smart-Auto-Data-Recharge',
  AUTO_RECHARGE_DISABLED_MESSAGE = 'Auto-Recharge-Disabled-Message'
}

/**
 * Maps application event properties to service-specific format
 *
 * @param eventName The standardized app event enum
 * @param properties The original event properties from the application
 * @returns Transformed properties ready for the analytics service
 */
export function mapEventProperties(
  _eventName: AppEvent,
  properties: EventProperties
): EventProperties {
  // Default: return properties unchanged
  return properties;

  // Example of property transformation (uncomment and modify as needed)
  /*
    switch (eventName) {
      case AppEvent.PURCHASE:
        return {
          item_id: properties.productId,
          value: properties.price,
          currency: properties.currencyCode || 'USD',
          ...properties
        };
      default:
        return properties;
    }
    */
}
